@if (preferencesForm(); as form) {
  <div class="container px-6 py-6 mx-auto">
    <form
      [formGroup]="form"
      class="flex flex-col gap-6 w-full max-w-4xl mx-auto"
    >
      <div class="flex bg-white rounded-md p-4">
        <div class="flex items-center gap-3">
          <bnd-badge
            color="info"
            size="sm"
          >
            <span class="font-semibold">v{{ version() }}</span>
          </bnd-badge>
          @if (apiDocUrl()) {
            <a
              [href]="apiDocUrl()"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 text-sm font-medium text-green-900 underline"
              data-testid="api-documentation-link"
            >
              <i class="pi pi-book"></i>
              <span>{{ 'USER_PREFERENCES.API_DOCUMENTATION' | translate }}</span>
            </a>
          }
          @if (functionalDocUrl()) {
            <a
              [href]="functionalDocUrl()"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-2 text-sm font-medium text-green-900 underline"
              data-testid="functional-documentation-link"
            >
              <i class="pi pi-question-circle"></i>
              <span>{{ 'USER_PREFERENCES.FUNCTIONAL_DOCUMENTATION' | translate }}</span>
            </a>
          }
        </div>
      </div>

      <fieldset class="border border-neutral-300 rounded-md bg-white p-4 md:p-6">
        <legend class="text-sm md:text-base px-2 py-1 text-white bg-green-900 font-bold rounded-md">
          {{ 'USER_PREFERENCES.LANGUAGE' | translate }}
        </legend>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <bnd-select
            [label]="'USER_PREFERENCES.LANGUAGE' | translate"
            formControlName="language"
            [items]="languages()"
            bindLabel="label"
            bindValue="value"
            data-testid="user-language"
          />
        </div>
      </fieldset>

      <fieldset class="border border-neutral-300 rounded-md bg-white p-4 md:p-6">
        <legend class="text-sm md:text-base px-2 py-1 text-white bg-green-900 font-bold rounded-md">
          {{ 'USER_PREFERENCES.DEFAULT_AREA_UNIT' | translate }}
        </legend>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <bnd-select
            [label]="'USER_PREFERENCES.DEFAULT_YIELD_UNIT' | translate"
            formControlName="defaultYieldUnit"
            [items]="defaultYieldUnits()"
            bindLabel="translation"
            bindValue="code"
            data-testid="default-yield-unit"
          />
          <bnd-select
            [label]="'USER_PREFERENCES.DEFAULT_AREA_UNIT' | translate"
            formControlName="defaultAreaUnit"
            [items]="defaultAreaUnits()"
            bindLabel="translation"
            bindValue="code"
            data-testid="default-area-unit"
          />
          <bnd-select
            class="md:col-span-2"
            [label]="'USER_PREFERENCES.DEFAULT_APPLICATION_UNIT' | translate"
            formControlName="defaultApplicationUnit"
            [items]="defaultApplicationUnits()"
            bindLabel="translation"
            bindValue="code"
            data-testid="default-application-unit"
          />
        </div>
      </fieldset>

      <fieldset class="border border-neutral-300 rounded-md bg-white p-4 md:p-6">
        <legend class="text-sm md:text-base px-2 py-1 text-white bg-green-900 font-bold rounded-md">
          {{ 'USER_PREFERENCES.GEOLOCATION_RADIUS' | translate }}
        </legend>
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 mt-2">
          <bnd-text-input
            class="md:col-span-3"
            [label]="'USER_PREFERENCES.GEOLOCATION_RADIUS' | translate"
            type="number"
            [min]="1"
            [max]="maxRadius()"
            formControlName="geolocationRadius"
            data-testid="geolocation-radius"
          />
          <bnd-select
            class="md:col-span-2"
            [label]="'USER_PREFERENCES.GEOLOCATION_RADIUS_UNIT' | translate"
            formControlName="geolocationRadiusUnit"
            [items]="geolocationRadiusUnitOptions()"
            [bindLabel]="optionLabelKey"
            [bindValue]="optionValueKey"
            data-testid="geolocation-radius-unit"
          />
          <bnd-select
            class="md:col-span-3"
            [label]="'USER_PREFERENCES.REGION' | translate"
            formControlName="region"
            [items]="availableRegionOptions()"
            [bindLabel]="optionLabelKey"
            [bindValue]="optionValueKey"
            data-testid="region"
          />
          <bnd-select
            class="md:col-span-2"
            [label]="'USER_PREFERENCES.CAMPAIGN' | translate"
            formControlName="campaignId"
            [items]="campaigns()"
            [displayClear]="true"
            bindLabel="key"
            bindValue="id"
            data-testid="campaign"
          />
        </div>
      </fieldset>

      <fieldset class="border border-neutral-300 rounded-md bg-white p-4 md:p-6">
        <legend class="text-sm md:text-base px-2 py-1 text-white bg-green-900 font-bold rounded-md">
          {{ 'USER_PREFERENCES.DISPLAY_CLOSE_CROPS' | translate }}
        </legend>
        <div class="flex flex-col gap-4 mt-2">
          <div class="flex items-center justify-between gap-4">
            <span class="text-sm font-medium">{{ 'USER_PREFERENCES.LIMIT_DISPLAY' | translate }}</span>
            <bnd-switch
              formControlName="limitDisplay"
              data-testid="limitDisplay"
            />
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-sm font-medium">{{ 'USER_PREFERENCES.DISPLAY_CLOSE_CROPS' | translate }}</span>
            <bnd-switch
              formControlName="displayCloseCrops"
              data-testid="displayCloseCrops"
            />
          </div>
        </div>
      </fieldset>

      <div class="flex flex-row flex-wrap gap-3 justify-end md:justify-between md:items-center">
        @if (showClearCache()) {
          <bnd-button
            class="w-auto"
            color="error"
            border="outline-solid"
            (click)="onClearCache()"
            data-testid="user-preferences-clear-cache"
          >
            <div class="flex items-center gap-2">
              <i class="pi pi-eraser"></i>
              <span>{{ 'ACTION.CLEAR' | translate }}</span>
            </div>
          </bnd-button>
        }
        <div class="flex flex-row flex-wrap gap-3 justify-end md:ml-auto">
          @if (logoutRoute()) {
            <a
              [routerLink]="logoutRoute()"
              class="inline-flex"
            >
              <bnd-button
                id="logout"
                color="default"
                border="outline-solid"
              >
                {{ 'ACTION.LOGOUT' | translate }}
              </bnd-button>
            </a>
          }

          <bnd-button
            (click)="onSave()"
            [disabled]="form.invalid || pending()"
            color="primary"
          >
            {{ 'ACTION.SAVE' | translate }}
          </bnd-button>
        </div>
      </div>
    </form>
  </div>
} @else {
  <div class="absolute inset-0 flex items-center justify-center bg-white/60">
    <bnd-loading size="lg" />
  </div>
}
